import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  useDashboardFetchWaves,
  useReportDashboardFetchWave,
  waveFetchState,
} from '@context/DashboardFetchWaveContext'
import type { RootState } from '@store/index'
import type { RangeKey } from '@dataTypes/dashboard.types'
import { DashboardSection } from '@constants/Dashboard/dashboard.enums'
import { useDashboardUtilizationData } from '@hooks/dashboard/useDashboardUtilizationData'
import type { DashboardMetricItemData } from '../../../../types/dashboardMetrics.types'
import {
  isDashboardDevMocksEnabled,
  withDashboardMetricShellValues,
} from '@utils/dashboardDevMocks.util'
import { mergeOwnerClinicalEfficiencyKpis } from '@utils/clinicalEfficiency.util'
import {
  getOverviewUtilizationRow1Metrics,
  getOwnerOverviewKpiMetrics,
} from './overviewMetrics.mockData'
import type { OverviewSectionWaves } from './overviewSectionWaves'

const APPOINTMENT_SECONDARY_METRIC_IDS = new Set([
  'hygiene-util',
  'same-day-tx',
  'pending-tx-value',
  'new-patients',
])

export type OverviewUtilizationWaves = Pick<
  OverviewSectionWaves,
  | 'scheduling'
  | 'clinicalEfficiency'
  | 'diagnosedTreatment'
  | 'caseAcceptance'
  | 'providerUtilization'
>

type UseOverviewUtilizationOptions = {
  range: RangeKey
  date: string
  hiddenSections: Set<string>
  waves?: OverviewUtilizationWaves
}

const DEFAULT_UTILIZATION_WAVES: OverviewUtilizationWaves = {
  scheduling: 0,
  clinicalEfficiency: 0,
  diagnosedTreatment: 0,
  caseAcceptance: 0,
  providerUtilization: 0,
}

function isVisible(
  section: DashboardSection,
  hiddenSections: Set<string>,
): boolean {
  return !hiddenSections.has(section)
}

export function useOverviewUtilization({
  range,
  date,
  hiddenSections,
  waves = DEFAULT_UTILIZATION_WAVES,
}: UseOverviewUtilizationOptions): {
  operatoriesSettled: boolean
  schedulingOverview: ReturnType<
    typeof useDashboardUtilizationData
  >['schedulingOverview']
  schedulingOverviewDataParams: ReturnType<
    typeof useDashboardUtilizationData
  >['schedulingOverviewDataParams']
  schedulingLoading: boolean
  appointmentSecondaryMetrics: DashboardMetricItemData[]
  caseAcceptanceOverview: ReturnType<
    typeof useDashboardUtilizationData
  >['caseAcceptanceOverview']
  caseAcceptanceLoading: boolean
  clinicalEfficiencyLoading: boolean
  diagnosedTreatmentLoading: boolean
  ownerOverviewKpiMetrics: DashboardMetricItemData[]
  /** True once the responses backing the owner KPI row have arrived. */
  ownerKpiHasPayload: boolean
  utilizationRow1StandaloneMetrics: DashboardMetricItemData[]
  displayUtilizationRow2Metrics: DashboardMetricItemData[]
  displayUtilizationRow3Metrics: DashboardMetricItemData[]
  /** True once the responses backing the utilization metric rows have arrived. */
  utilizationMetricsHasPayload: boolean
  providerUtilizationRows: ReturnType<
    typeof useDashboardUtilizationData
  >['providerUtilizationRows']
  providerUtilizationLoading: boolean
  providerUtilizationHasPayload: boolean
} {
  const useMocks = isDashboardDevMocksEnabled()
  const show = (section: DashboardSection): boolean =>
    isVisible(section, hiddenSections)

  const openWaves = useDashboardFetchWaves()
  const schedulingGate = waveFetchState(
    openWaves,
    show(DashboardSection.Appointments) ||
      show(DashboardSection.UtilizationMetrics) ||
      show(DashboardSection.SameDayCancellation) ||
      show(DashboardSection.AppointmentOverrun)
      ? waves.scheduling
      : null,
  )
  const clinicalEfficiencyGate = waveFetchState(
    openWaves,
    show(DashboardSection.UtilizationMetrics) ||
      show(DashboardSection.AvgProviderUtilization) ||
      show(DashboardSection.TreatmentOpportunities)
      ? waves.clinicalEfficiency
      : null,
  )
  const diagnosedTreatmentGate = waveFetchState(
    openWaves,
    show(DashboardSection.Diagnosis) ||
      show(DashboardSection.TreatmentAcceptance) ||
      show(DashboardSection.CaseAcceptance) ||
      show(DashboardSection.UtilizationMetrics)
      ? waves.diagnosedTreatment
      : null,
  )
  const caseAcceptanceGate = waveFetchState(
    openWaves,
    show(DashboardSection.CaseAcceptance) ||
      show(DashboardSection.TreatmentAcceptance) ||
      show(DashboardSection.UtilizationMetrics) ||
      show(DashboardSection.CaseAcceptanceRate)
      ? waves.caseAcceptance
      : null,
  )
  const providerUtilizationGate = waveFetchState(
    openWaves,
    waves.providerUtilization,
  )
  const utilizationFetchFlags = {
    scheduling: schedulingGate.enabled,
    providerUtilization: providerUtilizationGate.enabled,
    clinicalEfficiency: clinicalEfficiencyGate.enabled,
    diagnosedTreatment: diagnosedTreatmentGate.enabled,
    caseAcceptance: caseAcceptanceGate.enabled,
  }
  const utilizationQueuedFlags = {
    scheduling: schedulingGate.queued,
    providerUtilization: providerUtilizationGate.queued,
    clinicalEfficiency: clinicalEfficiencyGate.queued,
    diagnosedTreatment: diagnosedTreatmentGate.queued,
    caseAcceptance: caseAcceptanceGate.queued,
  }

  const operatoryListEarly = useSelector(
    (state: RootState) => state.operatory.data,
  )
  const operatoryLoadingEarly = useSelector(
    (state: RootState) => state.operatory.loading,
  )
  const [operatoriesSettled, setOperatoriesSettled] = useState(
    () => (operatoryListEarly?.length ?? 0) > 0,
  )
  useEffect(() => {
    const count = operatoryListEarly?.length ?? 0
    if (count > 0 || useMocks) {
      setOperatoriesSettled(true)
      return
    }
    if (!operatoryLoadingEarly) {
      setOperatoriesSettled(true)
    }
  }, [operatoryLoadingEarly, operatoryListEarly, useMocks])

  const {
    schedulingOverview,
    schedulingOverviewDataParams,
    schedulingLoading,
    appointmentSecondaryMetrics,
    clinicalEfficiencyMetrics,
    clinicalEfficiencyLoading,
    clinicalEfficiencyHasPayload,
    diagnosedTreatmentLoading,
    diagnosedTreatmentHasPayload,
    utilizationRow2Metrics,
    caseAcceptanceOverview,
    caseAcceptanceLoading,
    providerUtilizationRows,
    providerUtilizationLoading,
    providerUtilizationHasPayload,
    mergeRow1SchedulingMetrics,
  } = useDashboardUtilizationData({
    range,
    date,
    fetchFlags: utilizationFetchFlags,
    queuedFlags: utilizationQueuedFlags,
    operatoriesSettled,
  })

  const schedulingHeld = waves.scheduling != null && !operatoriesSettled
  useReportDashboardFetchWave(
    0,
    (waves.scheduling === 0 && (schedulingLoading || schedulingHeld)) ||
      (waves.clinicalEfficiency === 0 && clinicalEfficiencyLoading) ||
      (waves.caseAcceptance === 0 && caseAcceptanceLoading) ||
      (waves.diagnosedTreatment === 0 && diagnosedTreatmentLoading) ||
      (waves.providerUtilization === 0 && providerUtilizationLoading),
  )
  useReportDashboardFetchWave(
    1,
    (waves.scheduling === 1 && (schedulingLoading || schedulingHeld)) ||
      (waves.clinicalEfficiency === 1 && clinicalEfficiencyLoading) ||
      (waves.caseAcceptance === 1 && caseAcceptanceLoading) ||
      (waves.diagnosedTreatment === 1 && diagnosedTreatmentLoading) ||
      (waves.providerUtilization === 1 && providerUtilizationLoading),
  )
  useReportDashboardFetchWave(
    2,
    (waves.scheduling === 2 && (schedulingLoading || schedulingHeld)) ||
      (waves.clinicalEfficiency === 2 && clinicalEfficiencyLoading) ||
      (waves.caseAcceptance === 2 && caseAcceptanceLoading) ||
      (waves.diagnosedTreatment === 2 && diagnosedTreatmentLoading) ||
      (waves.providerUtilization === 2 && providerUtilizationLoading),
  )

  const ownerOverviewKpiMetrics = useMemo(() => {
    const metrics = withDashboardMetricShellValues(
      getOwnerOverviewKpiMetrics(range),
      useMocks,
    )
    const caseMetric = utilizationRow2Metrics.find(
      (metric) => metric.id === 'case-acceptance',
    )
    const withCaseAcceptance = caseMetric
      ? metrics.map((metric) =>
          metric.id === 'owner-case-acceptance-rate'
            ? {
                ...caseMetric,
                id: 'owner-case-acceptance-rate',
                leftLabel: 'Case acceptance rate',
              }
            : metric,
        )
      : metrics
    return mergeOwnerClinicalEfficiencyKpis(
      withCaseAcceptance,
      clinicalEfficiencyMetrics,
    )
  }, [useMocks, range, utilizationRow2Metrics, clinicalEfficiencyMetrics])

  const displayUtilizationRow2Metrics = useMemo(() => {
    const txOpportunities = clinicalEfficiencyMetrics.find(
      (metric) => metric.id === 'tx-opportunities',
    )
    return txOpportunities
      ? [...utilizationRow2Metrics, txOpportunities]
      : utilizationRow2Metrics
  }, [utilizationRow2Metrics, clinicalEfficiencyMetrics])

  const displayUtilizationRow3Metrics = useMemo(
    () =>
      clinicalEfficiencyMetrics.filter(
        (metric) => metric.id !== 'tx-opportunities',
      ),
    [clinicalEfficiencyMetrics],
  )

  const ownerKpiHasPayload =
    clinicalEfficiencyHasPayload && caseAcceptanceOverview != null
  const utilizationMetricsHasPayload =
    schedulingOverview != null &&
    clinicalEfficiencyHasPayload &&
    diagnosedTreatmentHasPayload &&
    caseAcceptanceOverview != null

  const appointmentsMetricsInSection = show(DashboardSection.Appointments)
  const providerUtilTableInSection = show(DashboardSection.ProviderUtilization)

  const utilizationRow1StandaloneMetrics = useMemo(() => {
    const all = withDashboardMetricShellValues(
      getOverviewUtilizationRow1Metrics(range),
      useMocks,
    )
    let filtered = all
    if (appointmentsMetricsInSection) {
      filtered = filtered.filter(
        (metric) => !APPOINTMENT_SECONDARY_METRIC_IDS.has(metric.id ?? ''),
      )
    }
    if (providerUtilTableInSection) {
      filtered = filtered.filter((metric) => metric.id !== 'provider-util')
    }
    return mergeRow1SchedulingMetrics(filtered)
  }, [
    useMocks,
    range,
    appointmentsMetricsInSection,
    providerUtilTableInSection,
    mergeRow1SchedulingMetrics,
  ])

  return {
    operatoriesSettled,
    schedulingOverview,
    schedulingOverviewDataParams,
    schedulingLoading,
    appointmentSecondaryMetrics,
    caseAcceptanceOverview,
    caseAcceptanceLoading,
    clinicalEfficiencyLoading,
    diagnosedTreatmentLoading,
    ownerOverviewKpiMetrics,
    ownerKpiHasPayload,
    utilizationRow1StandaloneMetrics,
    displayUtilizationRow2Metrics,
    displayUtilizationRow3Metrics,
    utilizationMetricsHasPayload,
    providerUtilizationRows,
    providerUtilizationLoading,
    providerUtilizationHasPayload,
  }
}
